import { Component, inject, Input, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { CannyService, FeatureFlagService } from '@galaxy/partner';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { FilterParams, HelpCenterService as HelpCenterAPIService, Origins } from '@vendasta/help-center';
import { combineLatest, firstValueFrom, of, take } from 'rxjs';
import { catchError, finalize, map, shareReplay, switchMap } from 'rxjs/operators';
import { AppConfigService } from '../app-config.service';
import { AssignedSalespersonService } from '../assigned-salesperson/assigned-salesperson.service';
import { HelpCaseId, HELP_PAGES, HELP_CASES } from './help-case-card/help-center-cases';
import { Article } from './help-search-card/help-center-articles';
import { HelpSideDrawerTemplateRefService } from './help-side-drawer/help-side-drawer-template-ref.service';
import { SupportNumberService } from '../common/help-menu';
import { FeatureFlags } from '../core/features';
import { SYSTEM_ASSISTANT_ID } from '@galaxy/ai-assistant/core';
import { PlatformLocation } from '@vendasta/conversation';
import { Router } from '@angular/router';

@Component({
  selector: 'app-help-center',
  templateUrl: './help-center.component.html',
  styleUrls: ['./help-center.component.scss'],
  standalone: false,
})
export class HelpCenterComponent implements OnInit {
  @Input() partnerId: string;
  @Input() subscriptionTier: string;

  readonly featureFlagService = inject(FeatureFlagService);
  readonly assignedSalespersonService = inject(AssignedSalespersonService);
  readonly helpCenterAPIService = inject(HelpCenterAPIService);
  readonly helpSideDrawerTemplateRefService = inject(HelpSideDrawerTemplateRefService);
  readonly translate = inject(TranslateService);
  readonly cannyService = inject(CannyService);
  readonly appConfigService = inject(AppConfigService);
  readonly snackBarService = inject(SnackbarService);
  readonly supportNumberService = inject(SupportNumberService);
  readonly router = inject(Router);

  readonly marketId$ = this.appConfigService.config$.pipe(map((config) => config.userMarket));

  helpHeader: string = HELP_PAGES[HelpCaseId.MAIN].header;
  helpCaseHistory: string[] = [HelpCaseId.MAIN];
  currentPageId: string = HelpCaseId.MAIN;
  searchBar: UntypedFormControl = new UntypedFormControl();
  articles: Article[] = [];
  showLoadMore: boolean;
  cursor: string;

  isSearching = false;
  title$ = this.translate.stream(['HELP_CENTER.TITLE']).pipe(map((translations) => translations['HELP_CENTER.TITLE']));
  readonly platformLocation = PlatformLocation.PLATFORM_LOCATION_PARTNER_CENTER;
  readonly assistantId = SYSTEM_ASSISTANT_ID;
  isOtherOptionsExpanded = false;

  readonly featureFlags$ = this.marketId$.pipe(
    switchMap((marketId) =>
      this.featureFlagService.batchGetStatus(this.partnerId, marketId, [
        FeatureFlags.INBOX_AI_SUPPORT_FEATURE_ID,
        FeatureFlags.INBOX_AI_ATLAS_LINK,
      ]),
    ),
    shareReplay(1),
  );

  readonly canShowAiSupport$ = this.featureFlags$.pipe(
    map(
      (featureFlagStatus) =>
        featureFlagStatus[FeatureFlags.INBOX_AI_SUPPORT_FEATURE_ID] &&
        featureFlagStatus[FeatureFlags.INBOX_AI_ATLAS_LINK],
    ),
    shareReplay(1),
  );

  readonly displaySearch$ = this.canShowAiSupport$.pipe(
    map(
      (canAccessInboxAiSupport) =>
        !canAccessInboxAiSupport &&
        (this.currentPageId === HelpCaseId.MAIN || this.currentPageId === HelpCaseId.SEARCH),
    ),
    shareReplay(1),
  );

  readonly supportPhoneNumber$ = this.supportNumberService.getSupportNumber().pipe(
    catchError((error) => {
      console.error(error);
      return of('');
    }),
  );

  readonly bookMeetingUrl$ = this.assignedSalespersonService.bookMeetingUrl$;

  readonly helpCases$ = combineLatest([this.canShowAiSupport$, this.supportPhoneNumber$, this.bookMeetingUrl$]).pipe(
    map(([canAccessInboxAiSupport, supportPhoneNumber, bookMeetingUrl]) => {
      const helpCases = canAccessInboxAiSupport
        ? HELP_PAGES[HelpCaseId.MAIN].cases
        : HELP_PAGES[HelpCaseId.MAIN].non_ai_supported_cases;

      return [
        ...(supportPhoneNumber ? [HELP_CASES[HelpCaseId.CALL_US]] : []),
        ...(bookMeetingUrl ? [HELP_CASES[HelpCaseId.BOOK_MEETING]] : []),
        ...helpCases,
      ];
    }),
    shareReplay(1),
  );

  ngOnInit(): void {
    this.isSearching = false;
    this.cursor = '';
  }

  search(onKeyUp = true): void {
    if (this.searchBar.value) {
      this.isSearching = true;
      if (onKeyUp) {
        this.articles = [];
      }

      const searchFilters: FilterParams = new FilterParams({
        origins: [
          Origins.ORIGINS_RESOURCE_CENTER_ZENDESK,
          Origins.ORIGINS_COMMUNITY_POST_ZENDESK,
          Origins.ORIGINS_PODCASTS_WP,
          Origins.ORIGINS_VIDEO_WP,
          Origins.ORIGINS_OPEN_API,
        ],
      });
      this.helpCenterAPIService
        .search(this.searchBar.value, 'en-us', searchFilters, this.cursor, 30)
        .pipe(
          map((resp) => {
            if (resp) {
              resp.results.forEach((r) => {
                // This keeps the types generic while we have no duplicate types from different sources
                r.origin = r.origin.replace(/(Conquer Local )|(Zendesk )/, '');
                this.articles.push(<Article>{ title: r.title, body: r.body, source: r.origin, link: r.link });
              });
              this.cursor = resp.nextCursor;
              this.showLoadMore = resp.hasMore;
            }
            if (this.currentPageId !== HelpCaseId.SEARCH) {
              this.currentPageId = HelpCaseId.SEARCH;
              this.helpCaseHistory.push(HelpCaseId.SEARCH);
            }
            return this.articles;
          }),
          finalize(() => {
            this.isSearching = false;
          }),
        )
        .subscribe();
    }
  }

  loadMoreClicked(): void {
    this.search(false);
  }

  async handleHelpCaseClick(caseId: string): Promise<void> {
    if (caseId === HelpCaseId.BOOK_MEETING) {
      const url = await firstValueFrom(this.bookMeetingUrl$);
      window.open(url, '_blank', 'noopener');
    }
    if (caseId === HelpCaseId.CANNY) {
      this.openCanny();
      return;
    }
    if (HELP_PAGES[caseId]) {
      if (HELP_PAGES[caseId].url) {
        window.open(HELP_PAGES[caseId].url, '_blank', 'noopener');
      } else {
        this.helpHeader = HELP_PAGES[caseId].header;
        if (caseId !== HelpCaseId.MAIN && caseId !== this.currentPageId) {
          this.currentPageId = caseId;
          this.helpCaseHistory.push(caseId);
        }
      }
    }
  }

  back(): void {
    this.helpCaseHistory.pop();
    this.currentPageId = this.helpCaseHistory.slice(-1)[0];
    this.handleHelpCaseClick(this.currentPageId);
  }

  openCanny(): void {
    this.appConfigService.config$
      .pipe(
        take(1),
        switchMap((appConfig) => {
          return this.cannyService.getSingleSignOn(appConfig.unifiedUserId);
        }),
      )
      .subscribe({
        next: (ssoConfig) => {
          window.open(
            this.buildCannyRedirectUrl(ssoConfig.token, ssoConfig.companyId, `https://${ssoConfig.roadmapDomain}`),
            '_blank',
            'noopener',
          );
        },
        error: () => {
          this.snackBarService.openErrorSnack(this.translate.instant('HELP_CENTER.ERROR'));
        },
      });
  }

  buildCannyRedirectUrl(ssoToken: string, companyId: string, redirect: string): string {
    return `https://canny.io/api/redirects/sso?companyID=${companyId}&ssoToken=${ssoToken}&redirect=${redirect}`;
  }

  chatWithAiButtonClicked(): void {
    this.helpSideDrawerTemplateRefService.close();
    this.router.navigate(['', { outlets: { inbox: 'inbox/ai' } }]);
  }

  toggleOtherOptions(): void {
    this.isOtherOptionsExpanded = !this.isOtherOptionsExpanded;
  }
}
